import { useEffect, useRef, useSyncExternalStore } from 'react'
import {
  isShakeToReportEnabled,
  needsMotionPermission,
  requestMotionPermission,
  subscribeShakeToReport,
  supportsShake,
} from '@/lib/shakeToReport'
import { preloadViewportCapture } from '@/lib/screenCapture'
import { createShakeDetector } from '@/lib/shakeDetector'
import { log } from '@/lib/logger'

/** The device-local shake-to-report setting, live across components. */
export function useShakeToReportEnabled(): boolean {
  return useSyncExternalStore(subscribeShakeToReport, isShakeToReportEnabled, () => false)
}

/** Calls `onShake` when the device is shaken hard, while shake-to-report is on. */
export function useShakeGesture(onShake: () => void) {
  const enabled = useShakeToReportEnabled()
  const onShakeRef = useRef(onShake)
  useEffect(() => {
    onShakeRef.current = onShake
  })

  useEffect(() => {
    if (!enabled || !supportsShake()) return
    preloadViewportCapture()

    const detect = createShakeDetector()
    let reportedFlowing = false
    let disposed = false

    function handleMotion(e: DeviceMotionEvent) {
      if (document.hidden) return
      // Gravity-free when the device has a linear-acceleration sensor; the
      // detector filters gravity out itself otherwise.
      const linear = e.acceleration
      const a = linear?.x != null ? linear : e.accelerationIncludingGravity
      if (a?.x == null || a.y == null || a.z == null) return
      if (!reportedFlowing) {
        reportedFlowing = true
        log('rage-shake', 'motion events flowing', { gravityFree: a === linear })
      }
      if (detect({ x: a.x, y: a.y, z: a.z, gravityFree: a === linear, t: e.timeStamp })) {
        log('rage-shake', 'shake detected')
        onShakeRef.current()
      }
    }

    function listen() {
      window.addEventListener('devicemotion', handleMotion)
    }

    // iOS needs the permission re-requested from a tap on every page load;
    // after the first grant it resolves without a prompt. Keeps trying until
    // a tap actually counts as a gesture.
    function armOnTap() {
      void requestMotionPermission().then(result => {
        if (disposed || result === 'error') return
        stopArming()
        log('rage-shake', 'motion permission', { result })
        if (result === 'granted') listen()
      })
    }
    function stopArming() {
      window.removeEventListener('click', armOnTap, true)
      window.removeEventListener('touchend', armOnTap, true)
    }

    if (needsMotionPermission()) {
      window.addEventListener('click', armOnTap, true)
      window.addEventListener('touchend', armOnTap, true)
    } else {
      listen()
    }
    return () => {
      disposed = true
      stopArming()
      window.removeEventListener('devicemotion', handleMotion)
    }
  }, [enabled])
}
